<template>
  <template v-if="canManage">
    <div v-if="loadingAccountCreator" class="flex justify-center py-8">
      <v-progress-circular indeterminate color="primary" width="2" size="24" />
    </div>
    <v-card v-else-if="accountCreator" class="px-6 pt-2 pb-6" elevation="3" rounded="lg">
      <div :class="{ 'mb-10': !accountCreatorHasPassedKycLiveness }">
        <h3 class="text-lg font-semibold mb-0 leading-tight">
          {{ t("treezorOnboarding.accountCreator.liveness.title") }}
        </h3>
        <p class="text-sm text-medium-emphasis m-0">
          {{ t("treezorOnboarding.accountCreator.liveness.hint") }}
        </p>
        <v-chip
          v-if="accountCreatorHasPassedKycLiveness"
          class="mt-3"
          size="large"
          color="success"
          variant="tonal"
          prepend-icon="mdi-check"
        >
          {{ t("treezorOnboarding.accountCreator.liveness.verified") }}
        </v-chip>
        <div
          v-else-if="accountCreatorKycLivenessStatus === 'started'"
          class="mt-3 flex items-center gap-2 text-sm text-medium-emphasis"
        >
          <v-progress-circular indeterminate size="20" width="2" color="primary" />
          {{ t("treezorOnboarding.accountCreator.liveness.started") }}
        </div>
        <v-alert
          v-else-if="accountCreatorKycLivenessStatus !== 'none'"
          class="mt-3"
          type="error"
          color="error"
        >
          <p class="font-semibold m-0">
            {{ t(`treezorOnboarding.accountCreator.liveness.${accountCreatorKycLivenessStatus}`) }}
          </p>
          <ul v-if="accountCreatorKycLivenessReasonCodes.length" class="m-0 ps-4">
            <li v-for="code in accountCreatorKycLivenessReasonCodes" :key="code">
              {{ t(`treezorOnboarding.accountCreator.liveness.reasons.${code}`) }}
            </li>
          </ul>
        </v-alert>
      </div>
      <template v-if="!accountCreatorHasPassedKycLiveness">
        <v-btn
          v-if="accountCreatorCanStartKycLiveness"
          color="primary"
          variant="flat"
          prepend-icon="mdi-play"
          :disabled="
            accountCreatorRelatedBusy ||
            !canEditKyc ||
            !user.isKind('user') ||
            !accountCreatorIdentityReady
          "
          @click="onStartKycLiveness"
        >
          {{
            t(
              accountCreatorKycLivenessStatus === "none"
                ? "treezorOnboarding.accountCreator.liveness.start"
                : "treezorOnboarding.accountCreator.liveness.retry",
            )
          }}
        </v-btn>
        <p v-if="showMissing" class="text-sm text-error mt-2 mb-0">
          {{ t("treezorOnboarding.accountCreator.liveness.missing") }}
        </p>
      </template>
    </v-card>

    <div v-if="!loadingAccountCreator" class="mt-6 flex justify-end gap-2">
      <v-btn
        class="me-auto"
        variant="text"
        prepend-icon="mdi-chevron-left"
        :disabled="accountCreatorRelatedBusy"
        @click="emit('previous')"
      >
        {{ t("common.previous") }}
      </v-btn>
      <v-btn
        color="primary"
        variant="flat"
        append-icon="mdi-chevron-right"
        :disabled="accountCreatorRelatedBusy"
        @click="emit('next')"
      >
        {{ t("common.next") }}
      </v-btn>
    </div>
  </template>
</template>

<script lang="ts" setup>
import { useI18n } from "@/composables/useI18n";
import { useUserStore } from "@/store";
import { computed } from "vue";
import { useTreezorOnboarding } from "../useTreezorOnboarding";

defineProps<{ showMissing: boolean }>();
const emit = defineEmits<{ previous: []; next: [] }>();

const { t } = useI18n();
const user = useUserStore();
const {
  canManage,
  canEditKyc,
  accountCreator,
  accountCreatorHasPassedKycLiveness,
  accountCreatorKycLivenessStatus,
  accountCreatorKycLivenessReasonCodes,
  accountCreatorCanStartKycLiveness,
  loadingAccountCreator,
  accountCreatorRelatedBusy,
  onStartKycLiveness,
} = useTreezorOnboarding();

const accountCreatorIdentityReady = computed(
  () =>
    !!accountCreator.value?.firstname.trim() &&
    !!accountCreator.value?.lastname.trim() &&
    !!accountCreator.value?.birthday,
);
</script>
